import { useCallback, useEffect, useMemo, useState } from "react";

import { format, formatDistanceToNow } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";

import {
  deviceIcon,
  fireDeviceLabel,
  parseDeviceUA,
  platformBadge,
} from "../utils/deviceUa";
import { flagEmoji, lookupIp } from "../utils/ipLookup";

export const groupPlaylistDevices = (devices = []) => {
  const map = new Map();

  for (const item of devices || []) {
    const key = item.userAgent || `__no-ua-${item.ipAddress || "no-ip"}`;
    const existing = map.get(key);

    if (!existing) {
      map.set(key, { ...item });
      continue;
    }

    existing.sessionCount =
      (existing.sessionCount || 0) + (item.sessionCount || 0);

    if (
      item.lastSeen &&
      (!existing.lastSeen ||
        new Date(item.lastSeen) > new Date(existing.lastSeen))
    ) {
      existing.lastSeen = item.lastSeen;
      if (item.ipAddress) existing.ipAddress = item.ipAddress;
      if (item.location) existing.location = item.location;
    }

    if (
      item.firstSeen &&
      (!existing.firstSeen ||
        new Date(item.firstSeen) < new Date(existing.firstSeen))
    ) {
      existing.firstSeen = item.firstSeen;
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const aT = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
    const bT = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;

    return bT - aT;
  });
};

const getPlaylistLabel = (playlist) =>
  playlist?.name ||
  playlist?.playlistName ||
  playlist?.accountName ||
  playlist?.username ||
  "Playlist";

const IpLookupPanel = ({ cache, ip, setCache }) => {
  const cached = cache[ip];
  const [state, setState] = useState(
    cached
      ? { loading: false, data: cached, error: null }
      : { loading: true, data: null, error: null }
  );

  const fetchLookup = useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    const resp = await lookupIp(ip);

    if (resp?.error) {
      setState({ loading: false, data: null, error: resp.error });
    } else {
      setState({ loading: false, data: resp, error: null });
      setCache((prev) => ({ ...prev, [ip]: resp }));
    }
  }, [ip, setCache]);

  useEffect(() => {
    if (!cached) fetchLookup();
  }, [cached, fetchLookup]);

  if (state.loading) {
    return (
      <div className="mt-2 rounded-lg p-3 text-[12px] text-gray-700 bg-gray-100 ring-1 ring-gray-200 dark:text-gray-400 dark:bg-black/30 dark:ring-white/10">
        <i className="fas fa-spinner fa-spin mr-2" />
        Looking up {ip}...
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="mt-2 rounded-lg p-3 text-[12px] text-gray-700 bg-gray-100 ring-1 ring-gray-200 dark:text-gray-400 dark:bg-black/30 dark:ring-white/10 flex items-center justify-between">
        <span>Lookup unavailable</span>
        <button
          className="underline text-gray-900 hover:text-black dark:text-gray-200 dark:hover:text-white"
          type="button"
          onClick={fetchLookup}
        >
          Retry
        </button>
      </div>
    );
  }

  const d = state.data || {};
  let localTime = null;

  if (d.timezone) {
    try {
      localTime = new Intl.DateTimeFormat(undefined, {
        timeZone: d.timezone,
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date());
    } catch {
      localTime = null;
    }
  }

  const rows = [];

  if (d.asn || d.org) {
    rows.push(["ASN", [d.asn, d.org].filter(Boolean).join(" - ")]);
  }

  rows.push([
    "Location",
    [[d.city, d.region].filter(Boolean).join(", "), d.postal, d.country]
      .filter(Boolean)
      .join(" - ") || "-",
  ]);

  if (d.timezone) {
    const off = d.utcOffset
      ? ` (UTC${d.utcOffset.slice(0, 3)}:${d.utcOffset.slice(3)})`
      : "";

    rows.push(["Timezone", `${d.timezone}${off}`]);
  }

  if (localTime) rows.push(["Local time", `${localTime} there`]);

  return (
    <div className="mt-2 rounded-lg p-3 text-[12px] bg-gray-100 ring-1 ring-gray-200 dark:bg-black/30 dark:ring-white/10">
      <div className="space-y-1.5">
        {rows.map(([label, value]) => (
          <div className="flex" key={label}>
            <div className="w-24 shrink-0 text-gray-600 dark:text-gray-500">
              {label}
            </div>
            <div className="text-gray-900 dark:text-gray-200">{value}</div>
          </div>
        ))}
      </div>
      <a
        className="inline-block mt-3 text-[11px] text-gray-600 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-300"
        href={`https://www.ip-tracker.org/lookup.php?ip=${encodeURIComponent(ip)}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        <i className="fas fa-external-link-alt mr-1" />
        Full report on ip-tracker.org
      </a>
    </div>
  );
};

const IpRow = ({ cache, ip, sendingMessage, setCache, onSendMessage }) => {
  const [expanded, setExpanded] = useState(false);

  const copy = (event) => {
    event.stopPropagation();
    try {
      navigator.clipboard
        .writeText(ip)
        .then(() => toast.success("IP copied"))
        .catch(() => toast.error("Could not copy"));
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-stretch rounded-md bg-gray-100 hover:bg-gray-200 ring-1 ring-gray-200 text-gray-800 dark:bg-white/5 dark:hover:bg-white/10 dark:ring-white/10 dark:text-gray-200 font-mono text-[12px] transition-colors overflow-hidden">
          <button
            aria-expanded={expanded}
            className="inline-flex items-center gap-2 px-2.5 py-1.5"
            type="button"
            onClick={() => setExpanded((value) => !value)}
          >
            <i className="fas fa-globe text-gray-500 dark:text-gray-500" />
            <span>{ip}</span>
            <i
              className={`fas fa-chevron-${expanded ? "up" : "down"} ml-1 text-gray-500 text-[10px]`}
            />
          </button>
          <button
            aria-label="Copy IP"
            className="px-2 text-gray-500 hover:text-gray-900 hover:bg-gray-200 dark:hover:text-gray-200 dark:hover:bg-white/10"
            title="Copy IP"
            type="button"
            onClick={copy}
          >
            <i className="fas fa-copy" />
          </button>
        </div>
        {onSendMessage && (
          <button
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium text-gray-800 bg-gray-100 hover:bg-gray-200 ring-1 ring-gray-200 dark:text-gray-200 dark:bg-white/5 dark:hover:bg-white/10 dark:ring-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={sendingMessage}
            title="Send a message to this user"
            type="button"
            onClick={onSendMessage}
          >
            {sendingMessage ? (
              <i className="fas fa-circle-notch fa-spin text-[11px] text-gray-400" />
            ) : (
              <i className="fas fa-paper-plane text-[11px] text-gray-400" />
            )}
            Send message
          </button>
        )}
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}
            transition={{ duration: 0.2 }}
          >
            <IpLookupPanel cache={cache} ip={ip} setCache={setCache} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DeviceCard = ({
  collapsible,
  expanded: expandedProp,
  lookupCache,
  device,
  sendingMessage,
  onSendMessage,
  onToggle,
  setLookupCache,
}) => {
  const parsed = parseDeviceUA(device.userAgent);
  const iconClass = deviceIcon(parsed.type);
  const fireLabel = fireDeviceLabel(parsed.model);
  const platform = platformBadge(parsed.platform);
  const loc = device.location;
  const expanded = collapsible ? !!expandedProp : true;

  const firstLoginAbs = device.firstSeen
    ? format(new Date(device.firstSeen), "MMM d, h:mm a")
    : "-";
  const lastActiveRel = device.lastSeen
    ? formatDistanceToNow(new Date(device.lastSeen), { addSuffix: true })
    : "-";
  const lastActiveAbs = device.lastSeen
    ? format(new Date(device.lastSeen), "MMM d, yyyy h:mm a")
    : "";
  const loginCount = device.sessionCount || 0;

  const header = (
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-gray-100 ring-1 ring-gray-200 text-gray-600 dark:bg-white/5 dark:ring-white/10 dark:text-gray-300">
        <i className={`fas ${iconClass} text-lg`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-semibold text-gray-900 dark:text-white">
            {parsed.app}
          </span>
          {parsed.version && (
            <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
              {parsed.version}
            </span>
          )}
          {platform && parsed.type !== "tv" && (
            <span
              className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ${platform.classes}`}
              title={
                parsed.platform
                  ? `Platform: ${parsed.platform}`
                  : "Platform could not be identified from the user agent"
              }
            >
              {platform.label}
            </span>
          )}
          {parsed.isEmulator && (
            <span
              className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 ring-1 ring-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30"
              title="User-agent matches a known emulator"
            >
              Emulator
            </span>
          )}
          {fireLabel && (
            <span
              className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 bg-orange-100 text-orange-800 ring-1 ring-orange-300 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/30"
              title={`Identified by model code ${parsed.model}`}
            >
              {fireLabel}
            </span>
          )}
        </div>
        <div className="text-sm mt-0.5 text-gray-600 dark:text-gray-500">
          {parsed.platform || "Unknown platform"}
          {parsed.platformVersion ? ` ${parsed.platformVersion}` : ""}
          {parsed.model ? ` - ${parsed.model}` : ""}
        </div>
      </div>

      {collapsible && (
        <div className="flex items-center gap-3 shrink-0 pt-1">
          <div className="hidden sm:flex items-center gap-3 text-[11px] text-gray-600 dark:text-gray-400">
            <span
              className="inline-flex items-center gap-1 tabular-nums"
              title={lastActiveAbs}
            >
              <i className="far fa-clock text-[10px] text-gray-500" />
              {lastActiveRel}
            </span>
            <span className="inline-flex items-center gap-1 tabular-nums">
              <i className="fas fa-sign-in-alt text-[10px] text-gray-500" />
              {loginCount.toLocaleString()}
            </span>
          </div>
          <i
            className={`fas fa-chevron-${expanded ? "up" : "down"} text-gray-500 text-[12px] transition-transform`}
          />
        </div>
      )}
    </div>
  );

  const body = (
    <>
      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <i className="fas fa-map-marker-alt text-gray-500 dark:text-gray-500" />
          {loc?.isPrivate ? (
            <span>Local network</span>
          ) : loc?.city && loc?.region ? (
            <span>
              {loc.city}, {loc.region}
              {loc.country ? (
                <>
                  {" - "}
                  {loc.countryCode && (
                    <span className="mr-1">{flagEmoji(loc.countryCode)}</span>
                  )}
                  {loc.country}
                </>
              ) : null}
            </span>
          ) : loc?.country ? (
            <span>{loc.country}</span>
          ) : (
            <span className="text-gray-500 dark:text-gray-500">
              Unknown location
            </span>
          )}
        </div>

        {device.ipAddress && !loc?.isPrivate && (
          <IpRow
            cache={lookupCache}
            ip={device.ipAddress}
            sendingMessage={sendingMessage}
            setCache={setLookupCache}
            onSendMessage={onSendMessage}
          />
        )}
        {device.ipAddress && loc?.isPrivate && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-500">
            <i className="fas fa-globe" /> Local network - no public IP lookup
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 grid grid-cols-3 gap-2 text-[12px] border-t border-gray-200 dark:border-white/10">
        <div>
          <div className="text-gray-600 dark:text-gray-500 uppercase tracking-wider text-[10px]">
            First login
          </div>
          <div className="mt-1 text-gray-900 dark:text-gray-200 tabular-nums">
            {firstLoginAbs}
          </div>
        </div>
        <div>
          <div className="text-gray-600 dark:text-gray-500 uppercase tracking-wider text-[10px]">
            Last active
          </div>
          <div
            className="mt-1 text-gray-900 dark:text-gray-200 tabular-nums"
            title={lastActiveAbs}
          >
            {lastActiveRel}
          </div>
        </div>
        <div>
          <div className="text-gray-600 dark:text-gray-500 uppercase tracking-wider text-[10px]">
            Logins
          </div>
          <div className="mt-1 text-gray-900 dark:text-white font-semibold tabular-nums">
            {loginCount.toLocaleString()}
          </div>
        </div>
      </div>
    </>
  );

  if (!collapsible) {
    return (
      <li className="rounded-xl p-5 bg-gray-50 ring-1 ring-gray-200 hover:ring-gray-300 dark:bg-white/5 dark:ring-white/10 dark:hover:ring-white/15 transition-colors">
        {header}
        {body}
      </li>
    );
  }

  return (
    <li className="rounded-xl bg-gray-50 ring-1 ring-gray-200 hover:ring-gray-300 dark:bg-white/5 dark:ring-white/10 dark:hover:ring-white/15 transition-colors overflow-hidden">
      <button
        aria-expanded={expanded}
        className="w-full text-left p-5 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-white/20"
        type="button"
        onClick={onToggle}
      >
        {header}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="px-5 pb-5">{body}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};

const PlaylistDevicesModal = ({
  devices = [],
  loading = false,
  onClose,
  onSendMessage,
  open,
  playlist,
  sendingMessage = false,
}) => {
  const groupedDevices = useMemo(
    () => groupPlaylistDevices(devices),
    [devices]
  );
  const [lookupCache, setLookupCache] = useState({});
  const [expandedDeviceKey, setExpandedDeviceKey] = useState(null);

  useEffect(() => {
    if (open) setExpandedDeviceKey(null);
  }, [open, playlist]);

  if (!open || !playlist) {
    return <AnimatePresence />;
  }

  const playlistLabel = getPlaylistLabel(playlist);
  const totalLogins = groupedDevices.reduce(
    (sum, item) => sum + (item.sessionCount || 0),
    0
  );

  return (
    <AnimatePresence>
      <motion.div
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        key="playlist-devices-modal"
        role="presentation"
        style={{ zIndex: 99999 }}
        transition={{ duration: 0.18 }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose?.();
          }
        }}
      >
        <motion.div
          animate={{ opacity: 1, scale: 1, y: 0 }}
          aria-modal="true"
          className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-[#0b0f1a] shadow-2xl ring-1 ring-gray-200 dark:ring-white/10"
          exit={{ opacity: 0, scale: 0.96, y: 10 }}
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          role="dialog"
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-gray-400/25 via-gray-500/10 to-transparent blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-gradient-to-tr from-gray-500/15 via-gray-400/5 to-transparent blur-3xl"
          />

          <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-[0.2em] mb-1.5">
                <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
                {devices.length === 0 ? "No Activity" : "Active Sessions"}
              </div>
              <h3 className="text-2xl font-bold tracking-tight">Devices</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {groupedDevices.length === 0
                  ? "No active devices"
                  : `${totalLogins.toLocaleString()} total ${
                      totalLogins === 1 ? "login" : "logins"
                    } across ${groupedDevices.length} ${
                      groupedDevices.length === 1 ? "device" : "devices"
                    }`}
                {" - "}
                <span className="text-gray-700 dark:text-gray-200 font-medium">
                  {playlistLabel}
                </span>
              </p>
            </div>
            <button
              aria-label="Close"
              className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-all"
              type="button"
              onClick={onClose}
            >
              <i className="fas fa-times text-sm" />
            </button>
          </div>

          <div className="relative px-6 pb-6 overflow-y-auto flex-1">
            {loading ? (
              <ul className="space-y-3">
                {[0, 1].map((i) => (
                  <li
                    className="rounded-xl p-5 animate-pulse bg-gray-50 ring-1 ring-gray-200 dark:bg-white/5 dark:ring-white/10"
                    key={i}
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-11 h-11 rounded-lg bg-gray-200 dark:bg-white/10" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 rounded w-2/5 bg-gray-200 dark:bg-white/10" />
                        <div className="h-2.5 rounded w-3/5 bg-gray-200 dark:bg-white/10" />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 grid grid-cols-3 gap-3 border-t border-gray-200 dark:border-white/10">
                      {[0, 1, 2].map((k) => (
                        <div className="space-y-1" key={k}>
                          <div className="h-2 rounded w-2/3 bg-gray-200 dark:bg-white/10" />
                          <div className="h-3 rounded w-3/4 bg-gray-200 dark:bg-white/10" />
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            ) : groupedDevices.length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-tv text-5xl mb-4 text-gray-300 dark:text-gray-700" />
                <p className="text-[14px] text-gray-500 dark:text-gray-400">
                  No devices have logged in to{" "}
                  <span className="text-gray-700 dark:text-gray-200 font-medium">
                    {playlistLabel}
                  </span>{" "}
                  yet.
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {groupedDevices.map((device, index) => {
                  const key = `${device.userAgent || "no-ua"}-${device.ipAddress || "no-ip"}-${index}`;
                  const collapsible = groupedDevices.length > 1;

                  return (
                    <DeviceCard
                      collapsible={collapsible}
                      device={device}
                      expanded={collapsible && expandedDeviceKey === key}
                      key={key}
                      lookupCache={lookupCache}
                      sendingMessage={sendingMessage}
                      setLookupCache={setLookupCache}
                      onSendMessage={onSendMessage}
                      onToggle={() =>
                        setExpandedDeviceKey((previous) =>
                          previous === key ? null : key
                        )
                      }
                    />
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlaylistDevicesModal;
